import { BAND_HEIGHTS } from '../core/profile/cssVars';
import { usePrediction } from '../core/prediction/usePrediction';
import { useTapToSpeak } from '../core/speech/useTapToSpeak';
import { ActivateButton } from './ActivateButton';
import { ContentBand } from './ContentBand';
import { KeepOffer, useKeepOffer } from './KeepOffer';
import { LayerSwitch } from './LayerSwitch';
import { ProtectiveGap } from './ProtectiveGap';
import { RestoreOffer, useRestoreOffer } from './RestoreOffer';
import { RepairRow } from './RepairRow';
import { UndoClearOffer, useUndoClearOffer } from './UndoClearOffer';
import { bandTenant } from './bandTenant';
import { useComposerStore } from '../core/composer/useComposer';
import { TapToSpeakOffer } from './TapToSpeakButton';
import type { Candidate } from '../core/prediction/PredictionProvider';

/**
 * The prediction row — the space Phase 1 reserved and left inert, now filled.
 *
 * **The height never changes.** It was 104 pt when it was empty and it is 104 pt now, because the
 * reservation existed precisely so that this moment could not move the keyboard. By the time
 * predictions appear the person has been using the device for weeks, and their motor memory for every
 * key position is worth more than the feature arriving on top of it. A flow test asserts the keyboard's
 * bounding box is unchanged with candidates present.
 *
 * Inside the content band, because a prediction is **search**, not recall (§5.2): the person has to
 * notice a word appearing and locate it, unlike a key whose position they already know. Candidates
 * therefore never span the full width even though the keyboard below them does.
 *
 * Slots hold still. `slots.ts` decides that, but it is visible here: a candidate keeps its position
 * while the person keeps typing, so the row can be glanced at rather than re-read.
 *
 * ── The frame, and its one permanent tenant (GEO-09, GEO-21, ADR-039) ──
 *
 * This component owns the `ContentBand`, the row and the height. Each of the four tenants used to wrap itself,
 * which was fine while a tenant was the only thing in the band and stopped being fine the moment something had
 * to sit *beside* one: the band's promise is that its height never changes, and a promise kept in four places
 * is a promise that gets broken in one of them.
 *
 * The layer switch takes the fifth flex unit and is **not** a tenant. It cannot be, and that is the whole
 * reason the frame exists: the band goes to the candidates as soon as anything is in the composer, so a switch
 * inside a tenant would vanish while typing — and after typing a digit the way back to the letters would
 * vanish with it. The tenant gets the remaining four fifths, which is what the four candidates and the four
 * repair phrases were already dividing between them.
 *
 * The keep offer (`AUT-05`, ADR-041) is the frame's second occupant and the first conditional one, at the
 * other end. While it is up the row divides into six units rather than five, so a repair phrase goes from
 * about 24.8 mm to about 21 mm — above the 16 mm floor, and whether the labels still *read* at that width
 * under an uncharacterised visual impairment is a device box rather than a calculation. The height does not
 * move, which is the promise this component exists to keep.
 */
export function PredictionRow({
  onAccept,
}: {
  /** Supplied by the surface, which owns the composer. Undefined while prediction is off. */
  onAccept?: (candidate: Candidate) => void;
} = {}): React.JSX.Element {
  const candidates = usePrediction((s) => s.candidates);
  const pendingSpeech = useTapToSpeak((s) => s.pending !== null);
  const record = usePrediction((s) => s.accept);
  const composerEmpty = useComposerStore((s) => s.state.text.trim() === '');
  const offerRestore = useRestoreOffer();
  const keepOffer = useKeepOffer();
  const offerUndoClear = useUndoClearOffer();

  const tenant = bandTenant({
    candidateCount: candidates.length,
    pendingSpeech,
    offerRestore,
    offerUndoClear,
    composerEmpty,
  });

  const accept = (candidate: Candidate): void => {
    onAccept?.(candidate);
    record(candidate);
  };

  return (
    <div
      data-testid="prediction-row"
      style={{ height: BAND_HEIGHTS.prediction, flex: '0 0 auto' }}
      // Empty is the resting state and must stay unremarkable: no border, no placeholder, nothing to
      // learn to ignore. Announced politely so a change is heard rather than only seen.
      aria-live="polite"
    >
      <ContentBand>
        <div style={{ display: 'flex', gap: 'var(--key-gap)', height: BAND_HEIGHTS.prediction }}>
          {/* The frame's other occupant, opposite the layer switch and conditional where the switch is
              permanent: it is up only while a spoken sentence has not been kept and nothing has been typed
              since (`AUT-05`, ADR-041). So it never coexists with the candidates, and it never displaces
              the repair row — which is wanted at exactly the same moment — because one is in the frame and
              one is the tenant. Its gap is the same rule as the switch's (GEO-10): keeping a sentence
              changes what the board holds, and the tenant beside it speaks. */}
          {keepOffer ? (
            <>
              <KeepOffer />
              <ProtectiveGap />
            </>
          ) : null}

          {/* Four fifths, for whichever tenant the precedence names. Six possible tenants, one precedence,
              documented in `bandTenant.ts`. Each arrived separately and for a good reason, which is exactly
              how components end up guessing at an order nobody wrote down — and then two of them appear at
              once in a band whose whole promise is that it never changes height. */}
          <div style={{ flex: '4 1 0', minWidth: 0, display: 'flex' }}>
            {tenant === 'tapToSpeak' ? <TapToSpeakOffer /> : null}
            {tenant === 'restoreOffer' ? <RestoreOffer /> : null}
            {tenant === 'undoClear' ? <UndoClearOffer /> : null}
            {tenant === 'repair' ? <RepairRow /> : null}
            {tenant === 'candidates' ? (
              <div style={{ display: 'flex', gap: 'var(--key-gap)', height: '100%', flex: '1 1 0' }}>
                {candidates.map((candidate) => (
                  <CandidateButton key={candidate.slot} candidate={candidate} onAccept={accept} />
                ))}
              </div>
            ) : null}
          </div>

          {/* The same rule as the bottom bar's, and the gap outlived the control it was put there for: on one
              side are things that speak, on the other is something that changes the board under the person's
              hands (GEO-10). */}
          <ProtectiveGap />
          <LayerSwitch />
        </div>
      </ContentBand>
    </div>
  );
}

function CandidateButton({
  candidate,
  onAccept,
}: {
  candidate: Candidate;
  onAccept: (candidate: Candidate) => void;
}): React.JSX.Element {
  return (
    <ActivateButton
      // Keyed by slot rather than by word, so the adapter's target id is stable while the row's
      // contents change. A target whose id moved under the person's finger would be a mis-hit waiting
      // to happen — and it is what the dwell and scanning modes in Phase 3 will bind to.
      id={`prediction:${candidate.slot}`}
      onActivate={() => onAccept(candidate)}
      label={candidate.word}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        minHeight: 0,
        fontSize: 'var(--font-button)',
        // A personal word looks different from a corpus word: the person's own vocabulary earning its
        // place is worth seeing, and it is a second channel besides position (§5.2).
        fontWeight: candidate.source === 'personal' ? 700 : 400,
        background: 'var(--key-bg)',
        color: 'var(--key-fg)',
        border: `3px ${candidate.source === 'bigram' ? 'dotted' : 'solid'} var(--key-border)`,
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {candidate.word}
    </ActivateButton>
  );
}
